import { Actions, UserActionTypes } from '../_actions/user.actions';
import { Error } from '../_models/auth.models';
import { Operations, OpTypes } from '../_models/general.model';
import { Role, UserProfile } from '../_models/user.models';

/**
 * State interface
 */
export interface UserState {
  profile: UserProfile | null;
  lastFetchedForAppKey: string | null;  // Track which appKey the profile was fetched for
  currentPlan: string | null;
  stripePortalLink: string | null;
  allPlans: Array<any> | null;
  subscribeResponse: any;
  defaultPaymentMethod: any;
  error: Error | null;
  roles: {
    [applicationId: number]: Array<Role>;
  };
  users: {
    [applicationId: number]: Array<UserProfile>;
  };
  operations: Operations;
  allPlansLoading:{[key:string]:boolean};
  rolesLoading:{[key:string]:boolean};
}

/**
 * Initials State
 */
export const initialState: UserState = {
  profile: null,
  lastFetchedForAppKey: null,
  error: null,
  users: {},
  operations: {},
  roles: {},
  currentPlan: null,
  defaultPaymentMethod: {},
  allPlans: [],
  stripePortalLink: null,
  subscribeResponse: null,
  allPlansLoading:{isLoading: false, isError: false},
  rolesLoading:{}
};

/**
 * Reducer
 */
export const UserReducer = (
  state = initialState,
  action: Actions
): UserState => {
  switch (action.type) {
    case UserActionTypes.PROFILE_SUCCESS:
      return {
        ...state,
        profile: action.payload,
        lastFetchedForAppKey: action.applicationKey ?? null,  // Track which app this profile is for
        error: null,
      };
    case UserActionTypes.UPDATE_PROFILE_SUCCESS:
      if(action.payload.FromSettings){
        return {
          ...state,
          error: null,
          operations: {
            ...state.operations,
            [action.refId]: {
              type: OpTypes.UPDATE,
              error: null,
            },
          },
        }
      } else {
        return {
          ...state,
          profile: {
            ...state.profile,
            ...action.payload,
          },
          error: null,
          operations: {
            ...state.operations,
            [action.refId]: {
              type: OpTypes.UPDATE,
              error: null,
            },
          },
        };
      }
    case UserActionTypes.SUBSCRIBE_PLAN_SUCCESS:
      return {
        ...state,
        error: null,
        subscribeResponse: action.payload,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.UPDATE,
            error: null,
          },
        },
      };
    case UserActionTypes.UPDATE_ACTIVE_SUCCESS:
    case UserActionTypes.TOGGLE_EMAIL_ALERTS_SUCCESS:
    case UserActionTypes.BULK_UPDATE_USERS_SUCCESS:
      return {
        ...state,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.UPDATE,
            payload:action.payload,
            error: null,
          },
        },
      };
    case UserActionTypes.UPDATE_PROFILE_FAILED:
    case UserActionTypes.VALIDATE_USER_EMAIL_FAILED:
    case UserActionTypes.UPDATE_USER_EMAIL_FAILED:
      return {
        ...state,
        error: action.payload,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.FAILED,
            error: action.payload,
          },
        },
      };
    case UserActionTypes.UPDATE_USER_EMAIL_SUCCESS:
    case UserActionTypes.VALIDATE_USER_EMAIL_SUCCESS:
      return {
        ...state,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.CREATE,
            payload: action.payload,
            error:null
          },
        },
      };
    case UserActionTypes.SUBSCRIBE_PLAN_FAILED:
      return {
        ...state,
        error: action.payload,
        subscribeResponse: action.payload,
        operations: {
          ...state.operations,
          [action.refId]: {
            type: OpTypes.FAILED,
            error: action.payload,
          },
        },
      };
    case UserActionTypes.LOAD_APPLICATION_ROLES_SUCCESS:
      return {
        ...state,
        roles: {
          ...state.roles,
          [action.applicationId]: action.payload,
        },
        rolesLoading:{...state.rolesLoading,[action.applicationId]:false, [`error-${action.applicationId}`]:false}
      };
    case UserActionTypes.LOAD_USERS_SUCCESS:
      return {
        ...state,
        users: {
          ...state.users,
          [action.applicationId]: action.payload,
        },
      };
    case UserActionTypes.LOAD_ALL_PLANS_SUCCESS:
      return {
        ...state,
        allPlans: action.payload,
        allPlansLoading:{isLoading: false, isError: false}
      };
    case UserActionTypes.LOAD_DEFAULT_PAYMENT_METHOD_SUCCESS:
      return {
        ...state,
        defaultPaymentMethod: action.payload,
      };
    case UserActionTypes.LOAD_CURRENT_PLAN_SUCCESS:
      return {
        ...state,
        currentPlan: action.payload,
      };
    case UserActionTypes.LOAD_STRIPE_PORTAL_SUCCESS:
      return {
        ...state,
        stripePortalLink: action.payload,
      };
    case UserActionTypes.LOAD_USERS:
    case UserActionTypes.LOAD_CURRENT_PLAN:
    case UserActionTypes.LOAD_DEFAULT_PAYMENT_METHOD:
    // case UserActionTypes.LOAD_ALL_PLANS:
    case UserActionTypes.LOAD_STRIPE_PORTAL:
    // case UserActionTypes.LOAD_APPLICATION_ROLES:
    case UserActionTypes.PROFILE:
    case UserActionTypes.UPDATE_PROFILE:
    case UserActionTypes.SUBSCRIBE_PLAN:
      return {
        ...state,
        error: null,
      };
    case UserActionTypes.LOAD_ALL_PLANS:
      return{
        ...state,
        error:null,
        allPlansLoading:{isLoading: true, isError: false}
      }
    case UserActionTypes.LOAD_APPLICATION_ROLES:
      return{
        ...state,
        error: null,
        rolesLoading:{...state.rolesLoading,[action.applicationId]:true,[`error-${action.applicationId}`]:false}
    }
    case UserActionTypes.LOAD_USERS_FAILED:
    // case UserActionTypes.LOAD_ALL_PLANS_FAILED:
    case UserActionTypes.LOAD_CURRENT_PLAN_FAILED:
    case UserActionTypes.LOAD_DEFAULT_PAYMENT_METHOD_FAILED:
    case UserActionTypes.LOAD_STRIPE_PORTAL_FAILED:
    // case UserActionTypes.LOAD_APPLICATION_ROLES_FAILED:
    case UserActionTypes.PROFILE_FAILED:
    case UserActionTypes.BULK_UPDATE_USERS_FAILED:
      return {
        ...state,
        error: action.payload,
      };
    case UserActionTypes.LOAD_ALL_PLANS_FAILED:
      return{
        ...state,
        error: action.payload,
        allPlansLoading:{isLoading: false, isError: true}

      }
    case UserActionTypes.LOAD_APPLICATION_ROLES_FAILED:
      return{
        ...state,
        error: action.payload,
        rolesLoading:{...state.rolesLoading,[action.applicationId]:false,[`error-${action.applicationId}`]:true}
      };
    case UserActionTypes.RESET:
      return initialState;
    default:
      return state;
  }
};
